// client/src/constants/leagueStatuses.js

/**
 * Centralized league status constants for the client application.
 *
 * This is the single source of truth for all league-related status constants
 * on the client side. These should be kept synchronized with server/constants/status.js
 */

/**
 * League status constants - named constants for individual statuses
 * Use these instead of hard-coded strings for type safety and consistency
 */
export const LEAGUE_STATUS = Object.freeze({
  DRAFT: 'draft',
  REGISTRATION_OPEN: 'registration_open',
  REGISTRATION_CLOSED: 'registration_closed',
  PENDING_START: 'pending_start',
  ACTIVE: 'active',
  IN_PROGRESS: 'in_progress',
  PLAYOFFS_SETUP: 'playoffs_setup',
  PLAYOFFS: 'playoffs',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
})

/**
 * All valid league statuses in order
 */
export const LEAGUE_STATUSES = Object.freeze([
  LEAGUE_STATUS.DRAFT,
  LEAGUE_STATUS.REGISTRATION_OPEN,
  LEAGUE_STATUS.REGISTRATION_CLOSED,
  LEAGUE_STATUS.PENDING_START,
  LEAGUE_STATUS.ACTIVE,
  LEAGUE_STATUS.IN_PROGRESS,
  LEAGUE_STATUS.PLAYOFFS_SETUP,
  LEAGUE_STATUS.PLAYOFFS,
  LEAGUE_STATUS.COMPLETED,
  LEAGUE_STATUS.CANCELED,
])

/**
 * League statuses that allow creating new divisions.
 */
export const DIVISION_CREATION_ALLOWED_STATUSES = Object.freeze([
  LEAGUE_STATUS.DRAFT,
  LEAGUE_STATUS.REGISTRATION_OPEN,
  LEAGUE_STATUS.REGISTRATION_CLOSED,
])

/**
 * League statuses that allow editing division assignments (moving players between divisions).
 */
export const DIVISION_ASSIGNMENT_EDIT_ALLOWED_STATUSES = Object.freeze([
  LEAGUE_STATUS.DRAFT,
  LEAGUE_STATUS.REGISTRATION_OPEN,
  LEAGUE_STATUS.REGISTRATION_CLOSED,
  LEAGUE_STATUS.PENDING_START,
])

/**
 * League statuses considered "post-registration" or "started" for edit restrictions.
 * Keep aligned with server/constants/status.js isLeagueStarted logic used in leagues.update.
 */
export const POST_REGISTRATION_STATUSES = Object.freeze([
  LEAGUE_STATUS.REGISTRATION_OPEN,
  LEAGUE_STATUS.REGISTRATION_CLOSED,
  LEAGUE_STATUS.PENDING_START,
  LEAGUE_STATUS.ACTIVE,
  LEAGUE_STATUS.PLAYOFFS_SETUP,
  LEAGUE_STATUS.PLAYOFFS,
  LEAGUE_STATUS.COMPLETED,
])

/**
 * League statuses considered "started" for deletion restrictions.
 * Only owner can delete leagues in these statuses.
 */
export const LEAGUE_STARTED_STATUSES = Object.freeze([
  LEAGUE_STATUS.PENDING_START,
  LEAGUE_STATUS.ACTIVE,
  LEAGUE_STATUS.PLAYOFFS,
  LEAGUE_STATUS.COMPLETED,
])

/**
 * Primary next-status mapping used by the client's single-action league status UI.
 * This intentionally mirrors the first transition option in the server canonical
 * LEAGUE_STATUS_TRANSITIONS map while preserving current client behavior.
 */
export const PRIMARY_NEXT_STATUS_BY_LEAGUE_STATUS = Object.freeze({
  [LEAGUE_STATUS.DRAFT]: LEAGUE_STATUS.REGISTRATION_OPEN,
  [LEAGUE_STATUS.REGISTRATION_OPEN]: LEAGUE_STATUS.REGISTRATION_CLOSED,
  [LEAGUE_STATUS.REGISTRATION_CLOSED]: LEAGUE_STATUS.PENDING_START,
  [LEAGUE_STATUS.PENDING_START]: LEAGUE_STATUS.ACTIVE,
  [LEAGUE_STATUS.ACTIVE]: LEAGUE_STATUS.PLAYOFFS_SETUP,
  [LEAGUE_STATUS.IN_PROGRESS]: LEAGUE_STATUS.PLAYOFFS_SETUP,
  [LEAGUE_STATUS.PLAYOFFS_SETUP]: LEAGUE_STATUS.PLAYOFFS,
  [LEAGUE_STATUS.PLAYOFFS]: LEAGUE_STATUS.COMPLETED,
})

/**
 * Check if a league status allows division creation
 * @param {string} status - League status to check
 * @returns {boolean} True if divisions can be created in this status
 */
export function canCreateDivisions(leagueStatus) {
  return DIVISION_CREATION_ALLOWED_STATUSES.includes(leagueStatus)
}

/**
 * Format a status string for display (e.g., "registration_open" -> "Registration Open")
 * @param {string} status - Status string to format
 * @returns {string} Formatted status string
 */
export function formatStatus(status) {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Generate a human-readable message for division creation restrictions.
 * @param {string} currentStatus - Current league status
 * @returns {string} User-friendly restriction message
 */
export function getDivisionCreationRestrictedMessage(currentStatus) {
  const allowedStatusesText =
    DIVISION_CREATION_ALLOWED_STATUSES.map(formatStatus).join(', ')

  return `Divisions are locked while league is in ${formatStatus(
    currentStatus,
  )} phase. Allowed phases for creating divisions: ${allowedStatusesText}.`
}

/**
 * Generate a human-readable message for division assignment restrictions.
 * @param {string} currentStatus - Current league status
 * @returns {string} User-friendly restriction message
 */
export function getDivisionAssignmentRestrictedMessage(currentStatus) {
  return `Division assignments are locked. Players cannot be moved between divisions when the league status is '${currentStatus}'. Division changes are only allowed during draft, registration, or pending start phases.`
}
